import React, { useState } from 'react';
import { Mail, ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronUp, Sparkles, UserCheck, AlertCircle, MoveRight, X, Check, ExternalLink } from 'lucide-react';
import { formatDate } from '../utils/dateHelpers';

/**
 * Item de email na timeline de uma oferta.
 *
 * Rules of Hooks: TODOS os useState declarados ANTES de qualquer return/condicional.
 * (Fix v2 após bug linha 180 — CLAUDE.md Defensive Coding Regra 5 aplicada)
 */
export const EmailTimelineItem = ({ email, ofertasDisponiveis, ofertaAtualId, onMoverOferta, onAbrirDetalhe }) => {
  // === TODOS os hooks primeiro ===
  const [expanded, setExpanded] = useState(false);
  const [moverOpen, setMoverOpen] = useState(false);
  const [ofertaEscolhida, setOfertaEscolhida] = useState('');
  const [movendo, setMovendo] = useState(false);

  // === Depois: early returns e lógica ===
  if (!email) return null;

  const isEntrada = email.direcao === 'entrada';
  const classifStatus = email.classificacao_status || 'pendente';
  const confianca = email.classificacao_confianca;

  const classifIndicator = (() => {
    if (classifStatus === 'humano_classificado') {
      return { icon: UserCheck, color: 'text-green-600', label: 'Classificado por humano' };
    }
    if (classifStatus === 'auto_classificado' && confianca >= 0.9) {
      return { icon: Sparkles, color: 'text-purple-500', label: `Auto-classificado (${email.classificado_por})` };
    }
    if (classifStatus === 'auto_classificado') {
      return { icon: Sparkles, color: 'text-amber-500', label: `Auto-classificado com baixa confiança` };
    }
    return null;
  })();

  const outrasOfertas = (ofertasDisponiveis || []).filter(o => o.id !== ofertaAtualId);

  const handleMoverConfirmar = async () => {
    if (!ofertaEscolhida) return;
    setMovendo(true);
    try {
      await onMoverOferta(email.id, ofertaEscolhida);
      setMoverOpen(false);
      setOfertaEscolhida('');
    } finally {
      setMovendo(false);
    }
  };

  const handleDesvincular = async () => {
    if (!window.confirm('Remover este email da oferta atual? Ele ficará aguardando nova classificação.')) return;
    setMovendo(true);
    try {
      await onMoverOferta(email.id, null);
    } finally {
      setMovendo(false);
    }
  };

  const directionBorder = isEntrada
    ? 'border-l-4 border-l-emerald-500 bg-emerald-50/30'
    : 'border-l-4 border-l-blue-500 bg-blue-50/30';

  return (
    <div className={`flex gap-2 py-3 pl-2 border-b border-slate-100 last:border-0 group ${directionBorder}`}>
      <div className="flex-shrink-0 mt-1">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
          isEntrada ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
        }`}>
          {isEntrada ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
        </div>
      </div>

      <div className="flex-grow min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-grow">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Mail size={11} className="text-slate-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-slate-800 truncate">
                {email.subject || '(sem assunto)'}
              </span>
              {classifIndicator && (
                <span title={classifIndicator.label}>
                  <classifIndicator.icon size={10} className={classifIndicator.color} />
                </span>
              )}
            </div>

            {/* De/Para/CC compacto com fundo cinza claro */}
            <div className="text-[11px] leading-relaxed text-slate-500 mt-1 mb-1.5 px-2 py-1.5 bg-slate-50 rounded-md">
              <div className="truncate">
                <span className="font-medium inline-block min-w-[28px]">De:</span>{' '}
                <span className="text-slate-700">{email.from_email || '—'}</span>
              </div>
              {Array.isArray(email.to_emails) && email.to_emails.length > 0 && (
                <div className="truncate">
                  <span className="font-medium inline-block min-w-[28px]">Para:</span>{' '}
                  <span className="text-slate-700">{email.to_emails.join(', ')}</span>
                </div>
              )}
              {Array.isArray(email.cc_emails) && email.cc_emails.length > 0 && (
                <div className="truncate">
                  <span className="font-medium inline-block min-w-[28px]">CC:</span>{' '}
                  <span className="text-slate-700">{email.cc_emails.join(', ')}</span>
                </div>
              )}
            </div>

            <div className="text-xs text-slate-500 truncate mt-1">
              {(email.body_text || '').slice(0, 150)}...
            </div>
          </div>
          <div className="text-[10px] text-slate-400 flex-shrink-0 whitespace-nowrap">
            {formatDate(email.data_envio)}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
          >
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {expanded ? 'Recolher' : 'Ler completo'}
          </button>

          {onAbrirDetalhe && (
            <button
              onClick={() => onAbrirDetalhe(email)}
              className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
              title="Abrir detalhes + editar vínculos"
            >
              <ExternalLink size={11} /> Abrir
            </button>
          )}

          {!moverOpen && outrasOfertas.length > 0 && (
            <button
              onClick={() => setMoverOpen(true)}
              className="text-[11px] text-slate-500 hover:text-indigo-600 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Este email pertence a outra oferta?"
            >
              <MoveRight size={11} /> Mover para outra oferta
            </button>
          )}
        </div>

        {moverOpen && (
          <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded flex items-center gap-1.5">
            <MoveRight size={12} className="text-indigo-600 flex-shrink-0" />
            <select
              value={ofertaEscolhida}
              onChange={(e) => setOfertaEscolhida(e.target.value)}
              className="flex-grow text-xs px-1.5 py-0.5 border border-slate-300 rounded bg-white"
              disabled={movendo}
            >
              <option value="">Escolher oferta...</option>
              {outrasOfertas.map(o => (
                <option key={o.id} value={o.id}>{o.nome}</option>
              ))}
            </select>
            <button
              onClick={handleMoverConfirmar}
              disabled={!ofertaEscolhida || movendo}
              className="px-1.5 py-0.5 bg-indigo-600 text-white rounded text-[11px] hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-0.5"
            >
              <Check size={10} /> {movendo ? '...' : 'Mover'}
            </button>
            <button
              onClick={handleDesvincular}
              disabled={movendo}
              className="px-1.5 py-0.5 bg-white border border-red-300 text-red-600 rounded text-[11px] hover:bg-red-50 disabled:opacity-50"
              title="Desvincular (fica pendente)"
            >
              Desvincular
            </button>
            <button
              onClick={() => { setMoverOpen(false); setOfertaEscolhida(''); }}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {expanded && (
          <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-700 whitespace-pre-wrap border border-slate-200">
            {email.body_text || '(sem conteúdo)'}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Bloco de emails ambíguos da empresa — aparece no final da timeline.
 *
 * Rules of Hooks: useState declarado ANTES de qualquer early return.
 */
export const EmailsAmbiguosBlock = ({ emailsAmbiguos, ofertaAtualId, onMoverOferta }) => {
  // === TODOS os hooks primeiro ===
  const [expanded, setExpanded] = useState(false);

  // === Depois: early returns ===
  if (!emailsAmbiguos || emailsAmbiguos.length === 0) return null;

  return (
    <div className="mt-4 border border-amber-200 bg-amber-50/50 rounded">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <AlertCircle size={14} className="text-amber-600" />
          <span className="text-xs font-semibold text-amber-900">
            {emailsAmbiguos.length} email{emailsAmbiguos.length !== 1 ? 's' : ''} da mesma empresa aguardando classificação
          </span>
        </div>
        {expanded ? <ChevronUp size={14} className="text-amber-600" /> : <ChevronDown size={14} className="text-amber-600" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-1.5">
          <div className="text-[11px] text-amber-700 italic mb-2">
            Estes emails chegaram da mesma empresa mas o sistema não conseguiu identificar automaticamente a qual oferta pertencem. Se algum é desta oferta atual, clique "Vincular aqui".
          </div>
          {emailsAmbiguos.map(email => (
            <AmbiguoRow
              key={email.id}
              email={email}
              ofertaAtualId={ofertaAtualId}
              onVincular={() => onMoverOferta(email.id, ofertaAtualId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Linha individual de email ambíguo.
 * Rules of Hooks: hook primeiro, return depois.
 */
const AmbiguoRow = ({ email, onVincular }) => {
  // === Hook primeiro ===
  const [processando, setProcessando] = useState(false);

  // === Lógica depois ===
  const handleVincular = async () => {
    setProcessando(true);
    try { await onVincular(); } finally { setProcessando(false); }
  };

  return (
    <div className="flex items-start gap-2 p-2 bg-white border border-amber-200 rounded">
      <div className="flex-grow min-w-0">
        <div className="text-xs font-medium text-slate-700 truncate">
          {email.from_email} · {formatDate(email.data_envio)}
        </div>
        <div className="text-xs text-slate-600 truncate mt-0.5">
          {email.subject || '(sem assunto)'}
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">
          {(email.body_text || '').slice(0, 120)}...
        </div>
      </div>
      <button
        onClick={handleVincular}
        disabled={processando}
        className="flex-shrink-0 px-2 py-1 bg-indigo-600 text-white rounded text-[11px] hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-0.5"
        title="Vincular este email à oferta atualmente aberta"
      >
        <Check size={10} /> {processando ? '...' : 'Vincular aqui'}
      </button>
    </div>
  );
};

export default EmailTimelineItem;
